// src/components/admin/payroll/components/PayrollSummaryDashboard.js
// Enhanced Summary Dashboard for 1000+ employees with smart validation categories

import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckCircle, AlertCircle, XCircle, Users, TrendingUp,
    ChevronDown, ChevronUp, RefreshCw, Play, Eye, Lock
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import payrollService from '../../../../services/payroll.service';

const PayrollSummaryDashboard = ({
    period,
    onCategoryClick,
    onProcessAll,
    onRefresh,
    isProcessing = false,
    isLocked = false
}) => {
    const { theme } = useTheme();
    const [validationSummary, setValidationSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCategory, setExpandedCategory] = useState(null);

    // Fetch validation summary from backend
    const fetchValidationSummary = useCallback(async () => {
        if (!period) return;

        setLoading(true);
        setError(null);

        try {
            const result = await payrollService.getValidationSummary(period);
            if (result.success) {
                setValidationSummary(result);
            } else {
                setError(result.error || 'Failed to fetch validation summary');
            }
        } catch (err) {
            console.error('Error fetching validation summary:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        fetchValidationSummary();
    }, [fetchValidationSummary]);

    // Format currency
    const formatCurrency = (amount) => {
        if (amount >= 10000000) {
            return `₹${(amount / 10000000).toFixed(2)} Cr`;
        } else if (amount >= 100000) {
            return `₹${(amount / 100000).toFixed(2)} L`;
        } else {
            return `₹${amount.toLocaleString('en-IN')}`;
        }
    };

    // Loading skeleton
    if (loading) {
        return (
            <div className={`rounded-xl shadow-sm p-6 ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
            }`}>
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-24 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={`rounded-xl shadow-sm p-6 ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
            }`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <XCircle className="w-6 h-6 text-red-500" />
                        <div>
                            <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Unable to load validation summary
                            </h3>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                {error}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={fetchValidationSummary}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const { summary = {}, categories = {} } = validationSummary || {};

    // Category configurations
    const categoryConfig = [
        {
            key: 'autoValidated',
            label: 'Auto-Validated',
            description: 'Ready to process - no review needed',
            icon: CheckCircle,
            color: 'green',
            bgLight: 'bg-green-50',
            bgDark: 'bg-green-900/30',
            borderLight: 'border-green-200',
            borderDark: 'border-green-700',
            textLight: 'text-green-700',
            textDark: 'text-green-400',
            iconBg: 'bg-green-100'
        },
        {
            key: 'needsReview',
            label: 'Needs Review',
            description: 'LOP, joiners, exits - review required',
            icon: AlertCircle,
            color: 'yellow',
            bgLight: 'bg-yellow-50',
            bgDark: 'bg-yellow-900/30',
            borderLight: 'border-yellow-200',
            borderDark: 'border-yellow-700',
            textLight: 'text-yellow-700',
            textDark: 'text-yellow-400',
            iconBg: 'bg-yellow-100'
        },
        {
            key: 'hasIssues',
            label: 'Has Issues',
            description: 'Missing data - fix required',
            icon: XCircle,
            color: 'red',
            bgLight: 'bg-red-50',
            bgDark: 'bg-red-900/30',
            borderLight: 'border-red-200',
            borderDark: 'border-red-700',
            textLight: 'text-red-700',
            textDark: 'text-red-400',
            iconBg: 'bg-red-100'
        }
    ];

    const canProcessAll = summary?.autoValidated > 0 && summary?.hasIssues === 0;

    return (
        <div className={`rounded-xl shadow-sm overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className={`text-xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {period} Payroll
                            </h2>
                            {isLocked && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                    <Lock className="w-3 h-3" />
                                    Processed
                                </span>
                            )}
                        </div>
                        <p className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            {summary?.total || 0} employees • {summary?.percentageAutoValidated || 0}% auto-validated
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => {
                                fetchValidationSummary();
                                onRefresh?.();
                            }}
                            className={`p-2 rounded-lg ${
                                theme === 'dark'
                                    ? 'hover:bg-gray-700 text-gray-400'
                                    : 'hover:bg-gray-100 text-gray-600'
                            }`}
                            title="Refresh"
                        >
                            <RefreshCw className="w-5 h-5" />
                        </button>
                        {!isLocked && (
                            <button
                                onClick={onProcessAll}
                                disabled={!canProcessAll || isProcessing}
                                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                                    canProcessAll && !isProcessing
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isProcessing ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <Play className="w-4 h-4" />
                                        Process All Validated
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Category Cards */}
            <div className="p-6">
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {categoryConfig.map((cat) => {
                        const Icon = cat.icon;
                        const count = categories?.[cat.key]?.count || 0;
                        const isExpanded = expandedCategory === cat.key;

                        return (
                            <div key={cat.key}>
                                <div
                                    onClick={() => {
                                        setExpandedCategory(isExpanded ? null : cat.key);
                                        if (count > 0) {
                                            onCategoryClick?.(cat.key);
                                        }
                                    }}
                                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                        theme === 'dark'
                                            ? `${cat.bgDark} ${cat.borderDark} hover:border-opacity-100`
                                            : `${cat.bgLight} ${cat.borderLight} hover:shadow-md`
                                    } ${count === 0 ? 'opacity-50 cursor-default' : ''}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className={`p-2 rounded-lg ${cat.iconBg}`}>
                                            <Icon className={`w-5 h-5 ${
                                                theme === 'dark' ? cat.textDark : cat.textLight
                                            }`} />
                                        </div>
                                        {count > 0 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onCategoryClick?.(cat.key);
                                                }}
                                                className={`text-xs px-2 py-1 rounded ${
                                                    theme === 'dark'
                                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                                }`}
                                            >
                                                <Eye className="w-3 h-3 inline mr-1" />
                                                View
                                            </button>
                                        )}
                                    </div>
                                    <div className="mt-3">
                                        <p className={`text-3xl font-bold ${
                                            theme === 'dark' ? cat.textDark : cat.textLight
                                        }`}>
                                            {count}
                                        </p>
                                        <p className={`text-sm font-medium ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            {cat.label}
                                        </p>
                                        <p className={`text-xs mt-1 ${
                                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                        }`}>
                                            {cat.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Quick Stats Bar */}
                <div className={`p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <Users className={`w-4 h-4 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`} />
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    <strong>{summary?.total || 0}</strong> Total Employees
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <TrendingUp className={`w-4 h-4 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`} />
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    <strong>{summary?.percentageValidated || summary?.percentageAutoValidated || 0}%</strong> Validated
                                </span>
                            </div>
                            {summary?.manuallyValidated > 0 && (
                                <div className="flex items-center gap-2">
                                    <CheckCircle className={`w-4 h-4 text-blue-500`} />
                                    <span className={`text-sm ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        <strong>{summary.manuallyValidated}</strong> Manually Approved
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Progress indicator */}
                        <div className="flex items-center gap-3">
                            <div className="w-32 h-2 rounded-full bg-gray-200 overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                                    style={{ width: `${summary?.percentageValidated || summary?.percentageAutoValidated || 0}%` }}
                                />
                            </div>
                            <span className={`text-xs font-medium ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                {(summary?.autoValidated || 0) + (summary?.manuallyValidated || 0)} / {summary?.total || 0}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Action Hints */}
                {summary?.hasIssues > 0 && (
                    <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${
                        theme === 'dark' ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
                    }`}>
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                            <strong>{summary.hasIssues} employees</strong> have issues that must be resolved before processing.
                            Click "Has Issues" above to view and fix.
                        </p>
                    </div>
                )}

                {summary?.needsReview > 0 && summary?.hasIssues === 0 && (
                    <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${
                        theme === 'dark' ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
                            <strong>{summary.needsReview} employees</strong> need review (LOP, joiners, exits).
                            You can process auto-validated employees now and review these separately.
                        </p>
                    </div>
                )}

                {canProcessAll && summary?.needsReview === 0 && (
                    <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${
                        theme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                    }`}>
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                            All <strong>{summary?.autoValidated}</strong> employees are validated and ready for processing!
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PayrollSummaryDashboard;
